// src/pages/Noticeboard.jsx
import React, { useEffect, useMemo, useState } from "react";

const API_BASE = process.env.REACT_APP_API_BASE;

function getToken() {
  return (
    localStorage.getItem("beercard_id_token") ||
    localStorage.getItem("beercard_token") ||
    localStorage.getItem("idToken") ||
    ""
  );
}

function getStoredUser() {
  try {
    const raw =
      localStorage.getItem("beercard_user") ||
      localStorage.getItem("teamkitty_user") ||
      localStorage.getItem("user");

    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function getRoleFromToken() {
  try {
    const token = getToken();
    if (!token) return localStorage.getItem("beercard_role") || "player";

    const [, payload] = token.split(".");
    const claims = JSON.parse(atob(payload));
    const groups = claims["cognito:groups"] || [];

    if (groups.includes("superadmin")) return "superadmin";
    if (groups.includes("admin")) return "admin";
    return localStorage.getItem("beercard_role") || "player";
  } catch {
    return localStorage.getItem("beercard_role") || "player";
  }
}

function getEmailFromToken() {
  try {
    const token = getToken();
    if (!token) return "";

    const [, payload] = token.split(".");
    const claims = JSON.parse(atob(payload));
    return claims.email || "";
  } catch {
    return "";
  }
}

function formatDate(value) {
  if (!value) return "";
  try {
    return new Date(value).toLocaleString("en-AU", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return value;
  }
}

export default function Noticeboard() {
  const storedUser = useMemo(() => getStoredUser(), []);
  const [profile, setProfile] = useState(storedUser);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const token = getToken();
  const role = getRoleFromToken();
  const currentEmail = profile.email || getEmailFromToken();
const teamId =
  profile.teamId ||
  profile.team ||
  localStorage.getItem("teamId") ||
  localStorage.getItem("beercard_teamId") ||
  localStorage.getItem("beercard_team");

const clubId =
  profile.clubId ||
  profile.club ||
  localStorage.getItem("clubId") ||
  localStorage.getItem("beercard_clubId") ||
  localStorage.getItem("beercard_club");

  const isAdmin = role === "admin" || role === "superadmin";

  async function apiFetch(path, options = {}) {
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "x-user-email": currentEmail || "",
        ...(options.headers || {}),
      },
    });

    const text = await res.text();
    let data = {};
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      data = { raw: text };
    }

    if (!res.ok) {
      throw new Error(data.error || data.details || `Request failed: ${res.status}`);
    }

    return data;
  }

  async function loadProfileIfNeeded() {
    if ((profile.teamId || profile.team) && (profile.clubId || profile.club) && profile.email) {
    return profile;
  }

    const user = await apiFetch("/user");
    setProfile(user);

    localStorage.setItem("beercard_user", JSON.stringify(user));
    if (user.teamId) localStorage.setItem("beercard_teamId", user.teamId);
    if (user.clubId) localStorage.setItem("beercard_clubId", user.clubId);

    return user;
  }

const loadMessages = async () => {
  try {
    setLoading(true);
    const user = await loadProfileIfNeeded();

    const resolvedTeamId = user.teamId || user.team || teamId;

    if (!resolvedTeamId) {
      throw new Error("Missing teamId for this user.");
    }

    const items = await apiFetch(
      `/noticeboard?teamId=${encodeURIComponent(resolvedTeamId)}`
    );

    setMessages(Array.isArray(items) ? items : []);
  } catch (err) {
    console.error("Failed to load noticeboard:", err);
    setError(err.message || "Failed to load noticeboard.");
  } finally {
    setLoading(false);
  }
};

useEffect(() => {
  loadMessages();
}, [teamId]);

  async function postMessage() {
    const trimmed = message.trim();
    if (!trimmed) return;

    setSaving(true);
    setError("");

    try {
      const user = await loadProfileIfNeeded();

      await apiFetch("/noticeboard", {
        method: "POST",
        body: JSON.stringify({
  message: trimmed,
  teamId: user.teamId || user.team || teamId,
  clubId: user.clubId || user.club || clubId,
}),
      });

      setMessage("");
      await loadMessages();
    } catch (err) {
      console.error("Failed to post message:", err);
      setError(err.message || "Failed to post message.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteMessage(messageId) {
    const confirmed = window.confirm("Remove this noticeboard comment?");
    if (!confirmed) return;

    setError("");

    try {
      await apiFetch(`/noticeboard/${encodeURIComponent(messageId)}`, {
        method: "DELETE",
      });

      setMessages((prev) => prev.filter((item) => item.messageId !== messageId));
    } catch (err) {
      console.error("Failed to delete message:", err);
      setError(err.message || "Failed to remove message.");
    }
  }

  const charsLeft = 300 - message.length;

  return (
    <main className="min-h-screen bg-[#F7F4EA] px-4 py-6 text-[#0B1C2C]">
      <div className="mx-auto max-w-3xl">
        <section className="mb-5 rounded-2xl bg-white p-5 shadow-md border border-[#0B1C2C]/10">
          <div className="mb-4">
            <h1 className="text-2xl font-bold">Team Noticeboard</h1>
            <p className="mt-1 text-sm text-gray-600">
              Share quick team updates, reminders and game day notes.
            </p>
          </div>

          {error && (
            <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value.slice(0, 300))}
            placeholder="Write a message for your team..."
            className="min-h-[110px] w-full rounded-xl border border-gray-300 p-3 text-sm outline-none focus:border-[#F59E0B] focus:ring-2 focus:ring-[#F59E0B]/25"
          />

          <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <span className={`text-xs ${charsLeft < 30 ? "text-red-600" : "text-gray-500"}`}>
              {charsLeft} characters left
            </span>

            <button
              type="button"
              onClick={postMessage}
              disabled={saving || !message.trim()}
              className="rounded-xl bg-[#F59E0B] px-5 py-2 text-sm font-bold text-[#0B1C2C] shadow-sm transition hover:bg-[#E58F00] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saving ? "Posting..." : "Post Message"}
            </button>
          </div>
        </section>

        <section className="space-y-3">
          {loading ? (
            <div className="rounded-2xl bg-white p-5 text-sm text-gray-600 shadow-md">
              Loading noticeboard...
            </div>
          ) : messages.length === 0 ? (
            <div className="rounded-2xl bg-white p-5 text-sm text-gray-600 shadow-md">
              No messages yet.
            </div>
          ) : (
            messages.map((item) => {
              const canDelete = isAdmin || item.email === currentEmail;

              return (
                <article
                  key={item.messageId || `${item.teamId}-${item.createdAt}`}
                  className="rounded-2xl bg-white p-5 shadow-md border border-[#0B1C2C]/10"
                >
                  <div className="mb-2 flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
                    <div className="min-w-0">
                      <p className="break-words text-base font-semibold text-[#0B1C2C]">
                        {item.message}
                      </p>
                      <p className="mt-2 text-xs text-gray-500">
                        {item.email || "Team member"} · {formatDate(item.createdAt)}
                      </p>
                    </div>

                    {canDelete && (
                      <button
                        type="button"
                        onClick={() => deleteMessage(item.messageId)}
                        className="self-start rounded-lg border border-red-200 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </article>
              );
            })
          )}
        </section>
      </div>
    </main>
  );
}
